import { useCallback, useMemo, useRef } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Button } from '@mesh.ai/rabbit';
import { getApplicableL2CompanyModules } from 'Deer/components/navigation/secondary/Company/CompanyModules';
import { L3_PATHS } from 'Deer/constants/routes';
import { CompensationIntentDropdown } from 'Deer/intent/Compensation/components/CompensationIntentDropdown';
import { Layout } from 'Deer/intent/components/Layout/Layout';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useHistory } from 'react-router-dom';

import { AgGridHeader } from 'uiToolkitV1/AgGridHeader';
import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { AgGridKebabMenuOptions } from 'uiToolkitV1/AgGridWrapperV2/types';
import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { TagColored } from 'uiToolkitV1/Tag';

import { COMPENSATION_CYCLES_DATA } from './dataHooks';
import { COMPENSATION_CYCLES_STATUS_COLORS, COMPENSATION_CYCLES_STATUS_LABELS, CompensationCycleData } from './types';

const StatusCellRenderer = ({ data }: ICellRendererParams) => {
  const { status } = data as CompensationCycleData;

  return (
    <TagColored color={COMPENSATION_CYCLES_STATUS_COLORS[status]}>
      {COMPENSATION_CYCLES_STATUS_LABELS[status]}
    </TagColored>
  );
};

export const CompensationSettings = () => {
  const agGridRef = useRef<AgGridReact>(null);
  const history = useHistory();
  const { moduleName } = useOrganisationSettings();
  const applicableL2CompanyModules = getApplicableL2CompanyModules(moduleName) ?? [];

  const columnDefs = useMemo<ColDef[]>(
    () => [
      { field: 'cycleName', headerName: 'Compensation cycle', flex: 2, filter: true, sortable: true },
      { field: 'preset', headerName: 'Preset', flex: 1.5, filter: true, sortable: true },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        filter: 'agSetColumnFilter',
        sortable: true,
        cellRenderer: StatusCellRenderer,
      },
      { field: 'launchDate', headerName: 'Launch date', flex: 1, filter: true, sortable: true },
    ],
    []
  );

  const kebabMenuOptionsGenerator = useCallback((data: CompensationCycleData) => {
    const options: AgGridKebabMenuOptions = [
      {
        label: 'Archive',
        onClick: () => console.log('Archive', data),
      },
      {
        label: 'Clone',
        onClick: () => console.log('Clone', data),
      },
    ];

    return options;
  }, []);

  const handleCreateNewCompensationCycle = () => {
    history.push(L3_PATHS.company.compensation.compensationCycleCreation.replace(':compensationCycleId', 'presets'));
  };

  return (
    <Layout
      Filters={
        <CompensationIntentDropdown
          breadcrumbs={{
            items: [
              {
                label: 'Company',
                to: applicableL2CompanyModules[0]?.value,
              },
            ],
            page: {
              label: 'Compensation',
              to: '',
            },
          }}
        />
      }
    >
      <Flex flexDirection="column">
        <PaperWithShadow p="12px" display="flex" flexDirection="column" gap="12px">
          <Flex justifyContent="flex-end">
            <Button onClick={handleCreateNewCompensationCycle}>Create new compensation cycle</Button>
          </Flex>
          <Flex minH="350px" w="100%">
            <AgGridTable
              // @ts-expect-error ignore
              ref={agGridRef}
              rowData={COMPENSATION_CYCLES_DATA}
              columnDefs={columnDefs}
              kebabMenuIsRowSpecific
              kebabMenuOptionsGenerator={kebabMenuOptionsGenerator}
              defaultColDef={{
                resizable: false,
                autoHeight: true,
                headerComponentParams: { enableMenu: true, enableSorting: true },
                menuTabs: ['filterMenuTab'],
                headerComponent: AgGridHeader,
                filter: 'agTextColumnFilter',
                cellStyle: { display: 'flex', alignItems: 'center', overflow: 'hidden' },
              }}
              pagination
              paginationPageSize={10}
              domLayout="autoHeight"
              containerAutoHeight
              headerHeight={40}
              rowHeight={44}
            />
          </Flex>
        </PaperWithShadow>
      </Flex>
    </Layout>
  );
};
